import React, { useState, useContext } from 'react';
import styled from '@emotion/styled';
import { OutboundLink, routes, Button, PageContext } from 'gatsby-theme-apollo';
import { Flex } from '../layout/flex';
import EditableText from '../text/editable-text';
import EntityThumbnail from './entity-thumbnail';

const EntityBox = styled.div`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  display: flex;
  cursor: pointer;
  box-sizing: border-box;
  border-radius: ${props => props.theme.radius};
  overflow: hidden;
  flex-shrink: 0;
  flex-grow: 1;
  flex: 1;
  border: 1px solid transparent;

  :hover {
    border: 1px solid ${props => props.theme.outlineFocused};

    span {
      background-color: rgba(255, 255, 255, 0.8);
      color: black;
    }
  }
`;

const EntityOutboundLink = EntityBox.withComponent(OutboundLink);

const EntityThumbnailLinkContainer = styled.div(props => ({
  margin: '2px'
}));

const StyledEntityList = styled.div`
  position: relative;
  border-radius: ${props => props.theme.radius};
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
`;

const EntityThumbnailLink = ({
  name,
  thumbnailURI,
  to,
  nameOutside,
  small,
}) => {
  return (
    <EntityThumbnailLinkContainer>
      <EntityOutboundLink href={to} title={`${name} More Info`}>
        <EntityThumbnail
          name={name}
          imgSrc={thumbnailURI}
          small={small}
        />
      </EntityOutboundLink>

    </EntityThumbnailLinkContainer>
  );
};

const EntityList = ({
  gameName,
  entities,
  entityType,
  limit,
  nameOutside,
  small,
  enableShowMore,
  enableSearch,
  showInterval = 12
}) => {
  const [entityLimit, setEntityLimit] = useState(limit);
  const [searchText, setSearchText] = useState('');
  const { logEvent } = useContext(PageContext);

  const entitiesFiltered =
    searchText !== ''
      ? entities.filter(({ name }) => name.toLowerCase().includes(searchText.toLowerCase()))
      : entities;

  let entitiesToDisplay = [...entitiesFiltered].slice(0, entityLimit);

  const increaseLimit = () => {
    logEvent('entity_list_show_more');
    setEntityLimit(entityLimit + showInterval);
    entitiesToDisplay = [...entitiesFiltered].slice(0, entityLimit);
  };

  return (
    <>
      {enableSearch && (
        <Flex
          style={{
            width: '100%',
            backgroundColor: `#000`,
            padding: `5px`,
            borderRadius: `5px`,
            justifyContent: `center`,
            overflow: `hidden`
          }}
        >
          <EditableText
            onInputChanged={newValue => {
              setSearchText(newValue);
            }}
            onFocus={() => {
              logEvent('entity_list_search_start');
            }}
            onBlur={() => {
              logEvent(`entity_list_search_complete`, { searchText });
            }}
            dark
            textValue={searchText}
            placeholder={`${gameName} ${entityType} Search`}
          />
        </Flex>
      )}
      <StyledEntityList limit={entityLimit}>
        {entitiesToDisplay &&
          entitiesToDisplay.map(entity => {
            return (
              <EntityThumbnailLink
                name={entity.name}
                thumbnailURI={entity.thumbnailURI}
                to={routes.Entity(gameName, entityType, entity.name)}
                nameOutside={nameOutside}
                small={small}
                key={entity.name}
              />
            );
          })}
      </StyledEntityList>
      <>
        {enableShowMore && (
          <Flex
            style={{
              width: `100%`,
              flexDirection: `column`,
              padding: `10px`
            }}
          >
            {enableSearch && searchText === '' && entityLimit < entities.length && (
              <Button primary onClick={() => increaseLimit()} style={{ marginBottom: `10px` }}>
                Show{' '}
                {entityLimit + showInterval >= entities.length ? `Rest` : `${showInterval} More`}
              </Button>
            )}
            {searchText === '' ? (
              <span>
                {entityLimit < entities.length
                  ? `Showing ${entityLimit} out of ${entities.length} `
                  : `Showing all `}
                {`${gameName} ${entityType}`}
              </span>
            ) : (
              <span>
                Showing {entitiesToDisplay.length} search matches out of {entities.length}{' '}
                {gameName} {entityType}
              </span>
            )}
          </Flex>
        )}
      </>
    </>
  );
};

export default EntityList;
export { StyledEntityList, EntityThumbnailLink };
